/**
 * Tearsheet de performance (porte de `utils/tearsheet.py`; backend em
 * `apps/graficos/tearsheet.py`). Mesma grade do legado, em cartões:
 *
 *     Performance acumulada | Estatísticas
 *     Retorno móvel         | Retorno mensal
 *     Drawdown              | Correlação (opcional)
 *
 * A série do retorno mensal é escolhida aqui, sem nova consulta. No retorno móvel, o
 * backend manda a janela padrão; as outras podem vir prontas ou marcadas `sob_demanda`,
 * e então são buscadas com `buscarMovel` quando escolhidas (payload enxuto).
 */
import { Alert, Loader, Select, SimpleGrid } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Grafico, type GraficoApi } from './Grafico';
import { Secao } from './Layout';
import { TabelaDados, type TabelaApi } from './TabelaDados';

type ComAviso<T> = T | { aviso: string };

export interface TearsheetApi {
  acumulada: GraficoApi;
  estatisticas: TabelaApi | null;
  moveis: ({ rotulo: string } & (ComAviso<{ grafico: GraficoApi }> | { sob_demanda: true }))[];
  janela_padrao: string;
  mensal: ({ serie: string } & ComAviso<{ tabela: TabelaApi }>)[];
  drawdown: ComAviso<{ grafico: GraficoApi }>;
  correlacao: ComAviso<{ grafico: GraficoApi }> | null;
}

function Aviso({ texto }: { texto: string }) {
  return <Alert color="yellow">{texto}</Alert>;
}

export type BlocoMovel = ComAviso<{ grafico: GraficoApi }>;

export function Tearsheet({
  dados,
  chave,
  buscarMovel,
}: {
  dados: TearsheetApi;
  /** Identifica o resultado (para o cache das janelas buscadas sob demanda). */
  chave?: unknown;
  buscarMovel?: (rotulo: string, sinal: AbortSignal) => Promise<BlocoMovel>;
}) {
  const [janela, setJanela] = useState(dados.janela_padrao);
  const [serie, setSerie] = useState(dados.mensal[0]?.serie ?? '');
  const escolhido = dados.moveis.find((m) => m.rotulo === janela) ?? dados.moveis[0];
  const sobDemanda = !!escolhido && 'sob_demanda' in escolhido;
  const buscado = useQuery({
    queryKey: ['tearsheet.movel', chave, escolhido?.rotulo],
    queryFn: ({ signal }) => buscarMovel!(escolhido!.rotulo, signal),
    enabled: sobDemanda && !!buscarMovel,
    staleTime: 5 * 60 * 1000,
  });
  const movel: BlocoMovel | undefined = sobDemanda
    ? buscado.data
    : (escolhido as BlocoMovel | undefined);
  const mensal = dados.mensal.find((m) => m.serie === serie) ?? dados.mensal[0];

  return (
    <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
      <Secao
        titulo="Performance acumulada"
        legenda="Retorno acumulado (%) desde o início de cada série."
      >
        <Grafico grafico={dados.acumulada} />
      </Secao>
      <Secao
        titulo="Estatísticas de performance"
        legenda="Retornos, volatilidade e drawdown em %; Sharpe sobre a taxa livre de risco."
      >
        {dados.estatisticas ? (
          <TabelaDados tabela={dados.estatisticas} alturaAutomatica />
        ) : (
          <Aviso texto="Nenhuma estatística de performance disponível." />
        )}
      </Secao>
      <Secao
        titulo="Retorno móvel"
        legenda="Retorno (%) acumulado na janela, em cada data."
        acoes={
          <Select
            aria-label="Janela do retorno móvel"
            data={dados.moveis.map((m) => m.rotulo)}
            value={escolhido?.rotulo ?? null}
            onChange={(v) => v && setJanela(v)}
            w={140}
            size="xs"
          />
        }
      >
        {buscado.isFetching && <Loader size="sm" />}
        {buscado.isError && <Aviso texto="Não foi possível calcular esta janela." />}
        {movel &&
          ('grafico' in movel ? (
            <Grafico grafico={movel.grafico} />
          ) : (
            <Aviso texto={movel.aviso} />
          ))}
      </Secao>
      <Secao
        titulo="Retorno mensal (%)"
        legenda="Por mês e no ano; verde e vermelho mais fortes até ±8%."
        acoes={
          dados.mensal.length > 1 && (
            <Select
              aria-label="Série do retorno mensal"
              data={dados.mensal.map((m) => m.serie)}
              value={mensal?.serie ?? null}
              onChange={(v) => v && setSerie(v)}
              w={200}
              size="xs"
            />
          )
        }
      >
        {mensal &&
          ('tabela' in mensal ? (
            <TabelaDados tabela={mensal.tabela} alturaAutomatica />
          ) : (
            <Aviso texto={mensal.aviso} />
          ))}
      </Secao>
      <Secao titulo="Drawdown" legenda="Queda (%) desde o pico anterior.">
        {'grafico' in dados.drawdown ? (
          <Grafico grafico={dados.drawdown.grafico} />
        ) : (
          <Aviso texto={dados.drawdown.aviso} />
        )}
      </Secao>
      {dados.correlacao && (
        <Secao titulo="Correlação" legenda="Correlação dos retornos diários.">
          {'grafico' in dados.correlacao ? (
            <Grafico grafico={dados.correlacao.grafico} />
          ) : (
            <Aviso texto={dados.correlacao.aviso} />
          )}
        </Secao>
      )}
    </SimpleGrid>
  );
}
